import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getDeviceTypes, createDeviceType, updateDeviceType, deleteDeviceType, type DeviceType } from '../api/deviceTypes'

interface DeviceTypeManagerProps {
  onClose?: () => void
}

const DEVICE_TYPE_ICONS = [
  '💡', '🔌', '🔘', '📡', '⚙️', '⚡', '🔗', '🔧', '📦', '🖥️', '📱', '🎛️', '🔊', '📺', '🌐', '🔋', '💻', '🖨️', '📷', '🎥'
]

const DEVICE_TYPE_COLORS = [
  '#FFD700', '#32CD32', '#FF6347', '#4169E1', '#8B4513', '#FF4500', '#228B22', '#708090', '#808080', '#FF69B4', '#00CED1', '#FF8C00', '#9370DB', '#20B2AA', '#FF1493', '#00FF7F', '#FFDAB9', '#DDA0DD', '#F0E68C', '#98FB98'
]

export default function DeviceTypeManager({ onClose }: DeviceTypeManagerProps) {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType | null>(null)

  const queryClient = useQueryClient()

  const { data: deviceTypes = [], isLoading, error } = useQuery({
    queryKey: ['deviceTypes'],
    queryFn: () => getDeviceTypes()
  })

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: '💡',
    color: '#FFD700',
    sort_order: 0,
    is_active: true
  })

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: '💡',
    color: '#FFD700',
    sort_order: 0,
    is_active: true
  })

  // Close all modals
  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setSelectedDeviceType(null)
  }

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAllModals()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // Mutations
  const createDeviceTypeMutation = useMutation({
    mutationFn: createDeviceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceTypes'] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
      setShowCreateForm(false)
      setCreateForm({
        name: '',
        code: '',
        description: '',
        icon: '💡',
        color: '#FFD700',
        sort_order: 0,
        is_active: true
      })
    },
    onError: (error) => {
      console.error('Error creating device type:', error)
      alert('Ошибка при создании типа устройства')
    }
  })

  const updateDeviceTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DeviceType> }) => 
      updateDeviceType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceTypes'] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
      setShowEditForm(false)
      setSelectedDeviceType(null)
    },
    onError: (error) => {
      console.error('Error updating device type:', error)
      alert('Ошибка при обновлении типа устройства')
    }
  })

  const deleteDeviceTypeMutation = useMutation({
    mutationFn: deleteDeviceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceTypes'] })
      queryClient.invalidateQueries({ queryKey: ['device-types-list'] })
    },
    onError: (error) => {
      console.error('Error deleting device type:', error)
      alert('Ошибка при удалении типа устройства')
    }
  })

  // Event handlers
  const handleCreateClick = () => {
    setShowCreateForm(true)
  }

  const handleEditClick = (deviceType: DeviceType) => {
    setSelectedDeviceType(deviceType)
    setEditForm({
      name: deviceType.name,
      code: deviceType.code,
      description: deviceType.description || '',
      icon: deviceType.icon || '💡',
      color: deviceType.color || '#FFD700',
      sort_order: deviceType.sort_order,
      is_active: deviceType.is_active
    })
    setShowEditForm(true)
  }

  const handleDeleteClick = (deviceType: DeviceType) => {
    if (confirm(`Вы уверены, что хотите удалить тип устройства "${deviceType.name}"?`)) {
      deleteDeviceTypeMutation.mutate(deviceType.id)
    }
  }

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    createDeviceTypeMutation.mutate(createForm)
  }

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedDeviceType) return
    
    updateDeviceTypeMutation.mutate({
      id: selectedDeviceType.id,
      data: editForm
    })
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Загрузка типов устройств...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-8 text-red-600">
            Ошибка загрузки типов устройств: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">🔧 Управление типами устройств</h2>
          <div className="flex space-x-2">
            <button
              onClick={handleCreateClick}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              ➕ Добавить тип
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                ✕ Закрыть
              </button>
            )}
          </div>
        </div>

        {/* Device Types Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Код
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Порядок
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deviceTypes.map((deviceType) => (
                <tr key={deviceType.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {deviceType.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {deviceType.code}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deviceType.sort_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-1">
                      <button
                        onClick={() => handleEditClick(deviceType)}
                        className="action-icon-btn edit"
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClick(deviceType)}
                        className="action-icon-btn delete"
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create Form Modal */}
        {showCreateForm && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeAllModals()
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Создать новый тип устройства</h3>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Название *</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      required
                      placeholder="Например: Светильники"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Код *</label>
                    <input
                      type="text"
                      value={createForm.code}
                      onChange={(e) => setCreateForm({ ...createForm, code: e.target.value })}
                      required
                      placeholder="Например: lighting"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Описание</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    rows={3}
                    placeholder="Описание типа устройства..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Иконка</label>
                    <div className="grid grid-cols-10 gap-1">
                      {DEVICE_TYPE_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setCreateForm({ ...createForm, icon })}
                          className={`w-8 h-8 rounded border text-lg ${
                            createForm.icon === icon 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Цвет</label>
                    <div className="grid grid-cols-5 gap-1">
                      {DEVICE_TYPE_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setCreateForm({ ...createForm, color })}
                          className={`w-8 h-8 rounded border-2 ${
                            createForm.color === color 
                              ? 'border-blue-500' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={createForm.is_active}
                      onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium">Активен</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={createDeviceTypeMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {createDeviceTypeMutation.isPending ? 'Создание...' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Form Modal */}
        {showEditForm && selectedDeviceType && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeAllModals()
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Редактировать тип устройства</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Название *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Код *</label>
                    <input
                      type="text"
                      value={editForm.code}
                      onChange={(e) => setEditForm({ ...editForm, code: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Описание</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Порядок сортировки</label>
                    <input
                      type="number"
                      value={editForm.sort_order}
                      onChange={(e) => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Иконка</label>
                    <div className="grid grid-cols-10 gap-1">
                      {DEVICE_TYPE_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setEditForm({ ...editForm, icon })}
                          className={`w-8 h-8 rounded border text-lg ${
                            editForm.icon === icon 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Цвет</label>
                    <div className="grid grid-cols-5 gap-1">
                      {DEVICE_TYPE_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditForm({ ...editForm, color })}
                          className={`w-8 h-8 rounded border-2 ${
                            editForm.color === color 
                              ? 'border-blue-500' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium">Активен</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={updateDeviceTypeMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateDeviceTypeMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  )
}
